import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Star, Building2 } from 'lucide-react';
import { Button } from './ui/Button';
export function PricingSection() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const tiers = [{
    name: 'Free',
    description: 'Get started with basic reconciliation',
    price: {
      monthly: 0,
      annual: 0
    },
    transactions: '500',
    integrations: '0',
    highlighted: false,
    badge: null,
    cta: 'Start Free',
    ctaDisable: false,
    features: ['500 transactions / month', 'Unlimited clients', 'Manual CSV uploads', 'Basic anomaly detection', 'No data storage (30-day retention)', 'Email Reconcilation report in Excel / PDF', 'Email / WhatsApp support']
  }, {
    name: 'Starter',
    description: 'For growing businesses',
    price: {
      monthly: 19,
      annual: 15
    },
    transactions: '2,000',
    integrations: '1',
    highlighted: true,
    badge: 'Most Popular',
    cta: 'Coming Soon',
    ctaDisable: true,
    features: ['2,000 transactions / month', 'Unlimited clients', '1 integration (QuickBooks, Xero, etc.)', 'Smart transaction matching', 'Anomaly detection & alerts', 'Export Reconcilation report Excel / PDF', 'Priority email support']
  }, {
    name: 'Professional',
    description: 'For teams that need more power',
    price: {
      monthly: 59,
      annual: 49
    },
    transactions: '10,000',
    integrations: '3',
    highlighted: false,
    badge: null,
    cta: 'Coming Soon',
    ctaDisable: true,
    features: ['10,000 transactions / month', 'Unlimited clients', '3 integrations included', 'AI-powered auto-matching', 'Advanced anomaly detection', 'Reconciliation reports', 'Duplicate detection', 'Live Reconciliation dashboard', 'Team collaboration (3 seats)', 'Priority chat & email support', 'Batch transaction uploads']
  }];
  return <section id="pricing" className="py-24 bg-white" id="pricing" data-id="element-345">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-346">
        {/* Header */}
        <div className="text-center mb-6" data-id="element-347">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-348">
            Simple, transaction-based pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-349">
            Pay based on the transactions you reconcile, not the number of
            clients. All plans include unlimited clients.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-3 mb-12" data-id="element-350">
          <span className={`text-sm font-medium ${billing === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-351">

            Monthly
          </span>
          <button onClick={() => setBilling(billing === 'monthly' ? 'annual' : 'monthly')} className={`relative w-12 h-6 rounded-full transition-colors ${billing === 'annual' ? 'bg-violet-600' : 'bg-gray-300'}`} data-id="element-352">

            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${billing === 'annual' ? 'translate-x-6' : ''}`} data-id="element-353" />

          </button>
          <span className={`text-sm font-medium ${billing === 'annual' ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-354">

            Annual
          </span>
          {billing === 'annual' && <span className="ml-1 bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-355">
              Save 17%
            </span>}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-356">
          {tiers.map(tier => <div key={tier.name} className={`relative rounded-2xl p-6 flex flex-col ${tier.highlighted ? 'bg-violet-600 text-white ring-4 ring-violet-200 shadow-xl scale-[1.02]' : 'bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow'}`} data-id="element-357">

              {tier.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2" data-id="element-358">
                  <span className="bg-violet-100 text-violet-700 text-xs font-bold px-3 py-1 rounded-full shadow-sm" data-id="element-359">
                    {tier.badge}
                  </span>
                </div>}

              <div className="mb-6" data-id="element-360">
                <h3 className={`text-lg font-bold mb-1 ${tier.highlighted ? 'text-white' : 'text-gray-900'}`} data-id="element-361">

                  {tier.name}
                </h3>
                <p className={`text-sm ${tier.highlighted ? 'text-violet-200' : 'text-gray-500'}`} data-id="element-362">

                  {tier.description}
                </p>
              </div>

              <div className="mb-6" data-id="element-363">
                <div className="flex items-baseline gap-1" data-id="element-364">
                  {tier.price[billing] === 0 ? <span className={`text-4xl font-bold ${tier.highlighted ? 'text-white' : 'text-gray-900'}`} data-id="element-365">

                      Free
                    </span> : <>
                      <span className={`text-4xl font-bold ${tier.highlighted ? 'text-white' : 'text-gray-900'}`} data-id="element-366">

                        ${tier.price[billing]}
                      </span>
                      <span className={`text-sm ${tier.highlighted ? 'text-violet-200' : 'text-gray-500'}`} data-id="element-367">

                        /month
                      </span>
                    </>}
                </div>
                {billing === 'annual' && tier.price.annual > 0 && <p className={`text-xs mt-1 ${tier.highlighted ? 'text-violet-200' : 'text-gray-400'}`} data-id="element-368">

                    Billed annually (${tier.price.annual * 12}/yr)
                  </p>}
              </div>

              {/* Key Stats */}
              <div className={`grid grid-cols-2 gap-3 mb-6 p-3 rounded-lg ${tier.highlighted ? 'bg-violet-500/40' : 'bg-gray-50'}`} data-id="element-369">

                <div data-id="element-370">
                  <p className={`text-xs font-medium uppercase tracking-wide ${tier.highlighted ? 'text-violet-200' : 'text-gray-500'}`} data-id="element-371">

                    Transactions
                  </p>
                  <p className={`text-sm font-bold ${tier.highlighted ? 'text-white' : 'text-gray-900'}`} data-id="element-372">

                    {tier.transactions}/mo
                  </p>
                </div>
                <div data-id="element-373">
                  <p className={`text-xs font-medium uppercase tracking-wide ${tier.highlighted ? 'text-violet-200' : 'text-gray-500'}`} data-id="element-374">

                    Integrations
                  </p>
                  <p className={`text-sm font-bold ${tier.highlighted ? 'text-white' : 'text-gray-900'}`} data-id="element-375">

                    {tier.integrations === '0' ? 'None' : tier.integrations}
                  </p>
                </div>
              </div>

              {/* CTA */}
              <Button variant={tier.highlighted ? 'outline' : tier.price[billing] === 0 ? 'outline' : 'primary'} fullWidth className={tier.highlighted ? 'bg-white text-violet-600 hover:bg-violet-50 border-white' : ''} disabled={tier.ctaDisable} data-id="element-376">
             
                <a href="https://tally.so/r/D4dgqb" target="_blank" rel="noopener noreferrer" data-id="element-377">
              
                  {tier.cta}

                </a> 
              </Button>

              {/* Features */}
              <ul className="mt-6 space-y-3 flex-grow" data-id="element-378">
                {tier.features.map((feature, i) => <li key={i} className="flex items-start gap-2" data-id="element-379">
                    <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${tier.highlighted ? 'text-violet-200' : 'text-violet-600'}`} data-id="element-380" />


                    <span className={`text-sm ${tier.highlighted ? 'text-violet-100' : 'text-gray-600'}`} data-id="element-381">

                      {feature}
                    </span>
                  </li>)}
              </ul>
            </div>)}
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-12" data-id="element-382">
          <p className="text-sm text-gray-500" data-id="element-383">
            All plans include{' '}
            <span className="font-bold text-gray-700" data-id="element-384">unlimited clients</span>.
            Need more transactions?{' '}
            <a href="#" className="text-violet-600 font-medium hover:text-violet-700" data-id="element-385">

              Contact us
            </a>{' '}
            for custom volume pricing.
          </p>
        </div>
      </div>
    </section>;
}